// Settings › Approvals and spend › Conversation approvals, per campaign and per step (Connect package 4; docs/specs/CONNECT.md section 18; E13).
// One card per campaign, grouped by module. Inside, each step of the campaign's path with what Ogma may send there in
// plain words ("send the booking email", "send the quote"), whether it goes automatically, as a sample or waits for
// you, and what it follows when the campaign sets nothing. Deal messages, and everything while the kill switch is on,
// are shown locked with the reason. Changing a cell is a person's (act set_autonomy_scope, crm_autonomy_scope_set);
// a proposal from the weekly review is accepted or declined in place (decide_autonomy_scope).
import { useId, useState } from "react";
import { Link } from "react-router-dom";
import { useCrm } from "../../lib/session";
import { useAsync } from "../../lib/useAsync";
import {
  approvalsBoard,
  campaignsByModule,
  campaignSummary,
  cellFor,
  decideScope,
  LEVEL_SHORT,
  LEVEL_WORD,
  looseSettings,
  sampleWords,
  ownSettings,
  setScope,
  stepItems,
  stepName,
  type ApprovalsBoard,
  type ApprovalsCampaign,
  type Level,
  type ScopeKind,
  type StepItem,
} from "../../lib/approvalsConnect";
import { Async, Notice } from "../../components/ui";
import { Button, Card } from "../../components/kit";

const STATUS: Record<string, string> = { approved: "Running", paused: "Paused", proposed: "Waiting for approval", draft: "Draft", retired: "Retired: clear its settings" };

export function ApprovalsGrid() {
  const crm = useCrm();
  const board = useAsync(() => approvalsBoard(crm), [crm]);
  const [notice, setNotice] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const changed = (text: string, tone: "ok" | "bad" = "ok") => {
    setNotice({ text, tone });
    board.reload();
  };
  const b = board.data;
  const subtitle = !b
    ? "Loading"
    : b.kill
      ? "Kill switch on: everything waits for you"
      : b.campaigns.length === 0
        ? "No campaigns yet"
        : `${b.campaigns.length} ${b.campaigns.length === 1 ? "campaign" : "campaigns"} · ${b.scopes.length ? `${b.scopes.length} ${b.scopes.length === 1 ? "setting" : "settings"} of their own` : "all follow the business"}`;
  return (
    <Card id="per-campaign" level={2} title="Per campaign and step" subtitle={subtitle} defaultOpen>
      <p className="muted">
        For each campaign, each step of its path and what Ogma may send there: automatically, as a sample ({sampleWords(b?.sample_share)} waits for you), or each one waiting for your approval. A
        campaign that sets nothing follows the business default.
      </p>
      <Notice text={notice?.text ?? null} tone={notice?.tone} />
      <Async state={board} label="Loading approvals per campaign">
        {(data) => (
          <>
            {data.kill && (
              <p className="notice notice-bad" role="status">
                <strong>The kill switch is on: everything waits for you.</strong> Switch it off in <Link to="/settings/approvals">Your approvals and spending</Link>.
              </p>
            )}
            {!data.gate_4_10 && (
              <p className="small">Drafts go without your approval in this business (4.10 is not one of your approval gates). A campaign can still ask you first, for each one or a sample.</p>
            )}
            {data.campaigns.length === 0 && (
              <p className="muted small">
                No campaigns yet. Make one on <Link to="/campaigns/list">Campaigns</Link>, and it gets its own approvals here.
              </p>
            )}
            {campaignsByModule(data.campaigns).map((g) => (
              <section key={g.module} aria-label={`${g.label} campaigns`}>
                <h3 className="field-label">{g.label}</h3>
                {g.campaigns.map((c) => (
                  <CampaignCard key={c.id} board={data} campaign={c} onChanged={changed} />
                ))}
              </section>
            ))}
            <p className="muted small">
              Whatever a campaign says, anything that mentions a price, a valuation or legal terms, a reply with a hand-off rule, a first message to someone new and deal messages wait for
              you. A complaint, a run of bounces or a failed spot check puts a campaign back to approving each one by itself.
              {Object.keys(data.find_approvals ?? {}).length > 0 && (
                <>
                  {" "}
                  Find campaigns&rsquo; own outreach (first emails, follow-ups and letters) is set on <Link to="/settings/approvals/outreach">Settings › Approvals and spend › Outreach approvals</Link>.
                </>
              )}
            </p>
          </>
        )}
      </Async>
    </Card>
  );
}

function CampaignCard({ board, campaign, onChanged }: { board: ApprovalsBoard; campaign: ApprovalsCampaign; onChanged: (text: string, tone?: "ok" | "bad") => void }) {
  const own = ownSettings(board, campaign);
  const [showAll, setShowAll] = useState(own > 0 && board.scopes.some((s) => s.play_id === campaign.id && s.step === ""));
  const path = campaign.path_label
    ? `Path: ${campaign.path_label}${campaign.path_from && campaign.path_from !== "campaign" ? " (the one it falls back to)" : ""}`
    : "No path yet";
  return (
    <Card
      id={`approvals-campaign-${campaign.id}`}
      variant="row"
      level={3}
      title={campaign.name}
      subtitle={`${STATUS[campaign.status] ?? campaign.status} · ${path} · ${campaignSummary(board, campaign)}`}
      defaultOpen={own > 0 || board.proposals.some((p) => p.play_id === campaign.id)}
    >
      <div className="table-wrap">
        <table className="stack approvals-table" aria-label={`Approvals for ${campaign.name}`}>
          <thead>
            <tr>
              <th scope="col">Step</th>
              <th scope="col">What Ogma does</th>
              <th scope="col">Now</th>
              <th scope="col">Change</th>
            </tr>
          </thead>
          <tbody>
            {showAll && <StepRows board={board} campaign={campaign} step="" onChanged={onChanged} />}
            {campaign.steps.map((step) => (
              <StepRows key={step} board={board} campaign={campaign} step={step} onChanged={onChanged} />
            ))}
          </tbody>
        </table>
      </div>
      <p className="small">
        <Button variant="link" small onClick={() => setShowAll((v) => !v)} aria-expanded={showAll}>
          {showAll ? "Hide the every-step settings" : "Set something for every step of this campaign"}
        </Button>
      </p>
    </Card>
  );
}

function StepRows({ board, campaign, step, onChanged }: { board: ApprovalsBoard; campaign: ApprovalsCampaign; step: string; onChanged: (text: string, tone?: "ok" | "bad") => void }) {
  const items = stepItems(step);
  return (
    <>
      {items.map((it, i) => (
        <ItemRow key={`${step}-${it.kind}-${i}`} board={board} campaign={campaign} step={step} item={it} first={i === 0} onChanged={onChanged} />
      ))}
    </>
  );
}

function ItemRow({ board, campaign, step, item, first, onChanged }: {
  board: ApprovalsBoard; campaign: ApprovalsCampaign; step: string; item: StepItem; first: boolean; onChanged: (text: string, tone?: "ok" | "bad") => void;
}) {
  const crm = useCrm();
  const id = useId();
  const cell = cellFor(board, campaign, step, item.kind);
  const [busy, setBusy] = useState(false);
  const where = `${campaign.name}, ${stepName(step).toLowerCase()}`;
  const change = async (value: string) => {
    setBusy(true);
    try {
      const level = value === "follow" ? null : (value as Level);
      const r = await setScope(crm, item.kind as ScopeKind, campaign.id, step, level);
      const back = r.pulled_back ? ` ${r.pulled_back} already approved by Ogma now wait for you.` : "";
      onChanged(`${where}: ${item.covers}: ${level ? LEVEL_WORD[level].toLowerCase() : "follows the default again"}.${back}`);
    } catch (e) {
      onChanged((e as Error).message, "bad");
    } finally {
      setBusy(false);
    }
  };
  const decide = async (accept: boolean) => {
    if (!cell.proposal) return;
    setBusy(true);
    try {
      const r = await decideScope(crm, cell.proposal.task_id, accept);
      onChanged(
        !accept
          ? `${where}: kept as it is.`
          : r.status === "promoted"
            ? `${where}: ${item.covers}: now ${LEVEL_WORD[r.level ?? cell.proposal.to].toLowerCase()}.`
            : `It no longer qualifies, so nothing changed${r.blockers?.length ? `: ${r.blockers.join("; ")}` : ""}.`,
        accept && r.status !== "promoted" ? "bad" : "ok",
      );
    } catch (e) {
      onChanged((e as Error).message, "bad");
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr className={first ? "step-first" : undefined}>
      <th scope="row" data-label="Step">
        {first ? stepName(step) : <span className="sr-only">{stepName(step)}</span>}
      </th>
      <td data-label="What Ogma does">{capitalise(item.covers)}</td>
      <td data-label="Now">
        {cell.locked ? (
          <>
            <strong>Always needs you</strong>
            <div className="muted small">{cell.locked}</div>
          </>
        ) : (
          <>
            {cell.own ? <strong>{LEVEL_SHORT[cell.level]}</strong> : <span>{LEVEL_SHORT[cell.level]}</span>}
            <div className="muted small">{cell.own ? `Set for ${step === "" ? "this campaign" : "this step"}. Without it: ${cell.inherits.charAt(0).toLowerCase()}${cell.inherits.slice(1)}` : cell.inherits}</div>
            {cell.note && <div className="muted small">{cell.note}</div>}
          </>
        )}
      </td>
      <td data-label="Change">
        {cell.locked ? (
          <span className="muted small">Locked</span>
        ) : (
          <>
            <label className="sr-only" htmlFor={id}>
              {`${capitalise(item.covers)} (${where})`}
            </label>
            <select id={id} value={cell.own?.level ?? "follow"} disabled={busy} onChange={(e) => void change(e.target.value)}>
              <option value="follow">Follow the default</option>
              {cell.choices.map((l) => (
                <option key={l} value={l}>
                  {LEVEL_WORD[l]}
                </option>
              ))}
            </select>
            {cell.proposal && (
              <div className="small">
                Ogma suggests: {LEVEL_WORD[cell.proposal.to].toLowerCase()}.{" "}
                <Button variant="link" small disabled={busy} onClick={() => void decide(true)}>
                  Yes
                </Button>{" "}
                <Button variant="link" small disabled={busy} onClick={() => void decide(false)}>
                  Not yet
                </Button>
              </div>
            )}
          </>
        )}
      </td>
    </tr>
  );
}

/**
 * Approvals on a path's step (Conversations › Routes; docs/specs/CONNECT.md 20.3): the same cells as the
 * grid above, for the campaigns that use this path, at this step. Changing one is the same people-only
 * set_autonomy_scope, with the same rules. The Approvals page stays the overview.
 */
export function StepApprovals({ pathKey, step }: { pathKey: string; step: string }) {
  const crm = useCrm();
  const board = useAsync(() => approvalsBoard(crm), [crm]);
  const [notice, setNotice] = useState<{ text: string; tone: "ok" | "bad" } | null>(null);
  const changed = (text: string, tone: "ok" | "bad" = "ok") => {
    setNotice({ text, tone });
    board.reload();
  };
  return (
    <fieldset className="choice-list step-approvals">
      <legend className="field-label">Approvals at this step</legend>
      <Notice text={notice?.text ?? null} tone={notice?.tone} />
      <Async state={board} label="Loading the approvals">
        {(data) => {
          const campaigns = data.campaigns.filter((c) => c.path_key === pathKey && c.path_from === "campaign");
          if (!campaigns.length) {
            return (
              <span className="muted small">
                No campaign has this path as its own, so the business defaults apply here. See <Link to="/settings/approvals/conversations">Approvals</Link>.
              </span>
            );
          }
          return campaigns.map((c) => (
            <div key={c.id} className="table-wrap">
              <table className="stack approvals-table" aria-label={`Approvals for ${c.name} at ${stepName(step)}`}>
                <caption className="small">{c.name}</caption>
                <thead>
                  <tr>
                    <th scope="col">Step</th>
                    <th scope="col">What Ogma does</th>
                    <th scope="col">Now</th>
                    <th scope="col">Change</th>
                  </tr>
                </thead>
                <tbody>
                  <StepRows board={data} campaign={c} step={step} onChanged={changed} />
                </tbody>
              </table>
            </div>
          ));
        }}
      </Async>
    </fieldset>
  );
}

function capitalise(s: string): string {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * "2 campaign settings still send by themselves": shown beside the business's own levels (Your approvals and spending, the
 * booking switch), because moving a kind back for the business leaves the campaigns' own settings as a person set
 * them. Links to the grid. Nothing when there are none.
 */
export function CampaignSettingsNote({ kinds }: { kinds?: ScopeKind[] }) {
  const crm = useCrm();
  const board = useAsync(() => approvalsBoard(crm), [crm]);
  if (board.error) {
    return (
      <p className="small muted" data-testid="campaign-settings-note" role="status">
        Couldn&rsquo;t check whether any campaigns still send by themselves. <Link to="/settings/approvals/conversations">See Settings › Approvals and spend › Conversation approvals</Link>
      </p>
    );
  }
  if (!board.data) return null;
  const loose = looseSettings(board.data, kinds);
  if (loose.length === 0) return null;
  const names = [...new Set(loose.map((l) => l.campaign))];
  return (
    <p className="small" data-testid="campaign-settings-note">
      <strong>
        {loose.length} campaign {loose.length === 1 ? "setting still sends" : "settings still send"} by {loose.length === 1 ? "itself" : "themselves"}
      </strong>{" "}
      ({names.slice(0, 3).join(", ")}
      {names.length > 3 ? ` and ${names.length - 3} more` : ""}), whatever the business default says.{" "}
      <Link to="/settings/approvals/conversations">See them in Settings › Approvals and spend › Conversation approvals</Link>
    </p>
  );
}
